import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  MicrophoneIcon,
  ScaleIcon,
} from '../../icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../services/adminAPI';
const ghanaMap = '/images/ghana-map.png';

const REGIONS = ['All', 'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Central', 'Volta', 'Northern'];
const REGION_COLORS = {
  'Greater Accra': '#5D87FF',
  Ashanti: '#13DEB9',
  Eastern: '#FFAE1F',
  Western: '#FA896B',
  Central: '#49BEFF',
  Northern: '#7B61FF',
  Volta: '#E91E8C',
};
const FALLBACK_COLORS = ['#5D87FF', '#13DEB9', '#FFAE1F', '#FA896B', '#49BEFF', '#7B61FF', '#E91E8C'];
const REGION_PINS = [
  { region: 'Greater Accra', top: '86%', left: '78%' },
  { region: 'Ashanti', top: '60%', left: '47%' },
  { region: 'Western', top: '88%', left: '30%' },
  { region: 'Eastern', top: '74%', left: '63%' },
  { region: 'Central', top: '86%', left: '52%' },
  { region: 'Northern', top: '33%', left: '60%' },
  { region: 'Volta', top: '73%', left: '84%' },
];

const formatHours = (value) => {
  const n = Number(value) || 0;
  if (n === 0) return '0h';
  if (n < 10) {
    const text = n.toFixed(1).replace(/\.0$/, '');
    return `${text}h`;
  }
  return `${Math.round(n).toLocaleString()}h`;
};

const formatAxis = (value) => {
  const n = Number(value) || 0;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  if (n >= 10) return String(Math.round(n));
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
};

const niceMax = (maxRaw) => {
  if (maxRaw <= 0) return 1;
  const padded = maxRaw * 1.15;
  const pow = 10 ** Math.floor(Math.log10(padded));
  const n = padded / pow;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * pow;
};

const KpiCard = ({ icon: Icon, title, value, sub, meta, progress, fill = 'primary' }) => {
  const fillClass = {
    primary: 'md-card-kpi-primary',
    dark: 'md-card-kpi-dark',
    warm: 'md-card-kpi-warm',
    slate: 'md-card-kpi-slate',
  }[fill] || 'md-card-kpi-primary';

  return (
    <div className={`md-card p-6 flex gap-4 items-start min-h-[148px] ${fillClass}`}>
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center bg-white/15 shadow-[inset_2px_2px_6px_rgba(0,0,0,0.2),inset_-2px_-2px_6px_rgba(255,255,255,0.12)]">
        {Icon && <Icon className="h-6 w-6 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-0.5">{title}</p>
        <p className="text-2xl font-bold leading-none mb-1 text-white">{value}</p>
        {sub && <p className="text-xs text-white/70">{sub}</p>}
        {meta && <p className="text-xs mt-1 text-white/65">{meta}</p>}
        {progress !== undefined && (
          <div className="mt-2">
            <div className="h-1.5 rounded-full bg-black/20 shadow-[inset_1px_1px_2px_rgba(0,0,0,0.25)]">
              <div className="h-1.5 rounded-full bg-white/90" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs mt-1 text-white/65">{progress}% Used</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SessionBadge = ({ status }) => {
  const map = {
    live: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', label: 'Live Recording' },
    processing: { bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', label: 'Processing' },
    idle: { bg: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400', label: 'Idle' },
    maintenance: { bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Maintenance' },
  };
  const s = map[status] || map.idle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const TenantStatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
    inactive: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map[status] || map.inactive}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const StackedBarChart = ({ data, dark }) => {
  const [tooltip, setTooltip] = useState(null);
  const W = 640;
  const H = 280;
  const pad = { top: 16, bottom: 28, left: 36, right: 8 };
  const inner = { w: W - pad.left - pad.right, h: H - pad.top - pad.bottom };
  const rows = data.length ? data : [{ label: '—', superior: 0, lower: 0 }];
  const maxRaw = Math.max(...rows.map((d) => d.superior + d.lower), 0);
  const maxVal = niceMax(maxRaw);
  const tickCount = 8;
  const step = maxVal / (tickCount - 1);
  const yTick = Array.from({ length: tickCount }, (_, i) => Math.round(i * step));
  const barW = inner.w / rows.length;
  const bw = Math.max(barW * 0.52, 5);
  const toY = (v) => inner.h - (v / maxVal) * inner.h;
  const labelEvery = Math.ceil(rows.length / 6);

  return (
    <div className="relative w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ minWidth: 420, minHeight: 260 }} onMouseLeave={() => setTooltip(null)}>
        {yTick.map((v) => {
          const y = pad.top + toY(v);
          return (
            <g key={v}>
              <line x1={pad.left} x2={W - pad.right} y1={y} y2={y} stroke={dark ? '#374151' : '#f3f4f6'} strokeWidth="1" />
              <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="9" fill={dark ? '#6b7280' : '#9ca3af'}>
                {formatAxis(v)}
              </text>
            </g>
          );
        })}
        {rows.map((d, i) => {
          const x = pad.left + i * barW + (barW - bw) / 2;
          const lowerH = (d.lower / maxVal) * inner.h;
          const supH = (d.superior / maxVal) * inner.h;
          const baseY = pad.top + inner.h;
          return (
            <g key={d.label} style={{ cursor: 'pointer' }} onMouseEnter={() => setTooltip({ i, d, x: x + bw / 2 })}>
              <rect x={x} y={baseY - lowerH} width={bw} height={lowerH} rx="2" fill="#13DEB9" opacity={tooltip?.i === i ? 1 : 0.9} />
              <rect x={x} y={baseY - lowerH - supH} width={bw} height={supH} rx="2" fill="#5D87FF" opacity={tooltip?.i === i ? 1 : 0.9} />
              {i % labelEvery === 0 && (
                <text x={x + bw / 2} y={H - 6} textAnchor="middle" fontSize="8" fill={dark ? '#6b7280' : '#9ca3af'}>
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
        {tooltip && (
          <g>
            <rect
              x={Math.min(tooltip.x - 50, W - 110)}
              y={pad.top}
              width="100"
              height="46"
              rx="4"
              fill={dark ? '#1f2937' : '#ffffff'}
              stroke={dark ? '#374151' : '#e5e7eb'}
              strokeWidth="1"
            />
            <text x={Math.min(tooltip.x - 50, W - 110) + 8} y={pad.top + 14} fontSize="8" fill="#5D87FF">
              Superior: {formatHours(tooltip.d.superior)}
            </text>
            <text x={Math.min(tooltip.x - 50, W - 110) + 8} y={pad.top + 28} fontSize="8" fill="#13DEB9">
              Lower: {formatHours(tooltip.d.lower)}
            </text>
            <text x={Math.min(tooltip.x - 50, W - 110) + 8} y={pad.top + 40} fontSize="8" fill={dark ? '#9ca3af' : '#6b7280'}>
              Total: {formatHours(tooltip.d.superior + tooltip.d.lower)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

const DonutChart = ({ segments, total, dark }) => {
  const [hovered, setHovered] = useState(null);
  const size = 240;
  const r = 86;
  const cx = 120;
  const cy = 120;
  const stroke = 28;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const slices = segments.map((s) => {
    const dash = (s.pct / 100) * circ;
    const gap = circ - dash;
    const slice = { ...s, dash, gap, offset };
    offset += dash;
    return slice;
  });
  const active = hovered !== null ? segments[hovered] : null;

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={dark ? '#1f2937' : '#f3f4f6'} strokeWidth={stroke} />
          {slices.map((s, i) => (
            <circle
              key={s.region}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={hovered === i ? stroke + 4 : stroke}
              strokeDasharray={`${s.dash} ${s.gap}`}
              strokeDashoffset={-s.offset + circ * 0.25}
              style={{ cursor: 'pointer', transition: 'stroke-width .15s' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className={`text-xs font-medium ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Total Hours</p>
          <p className={`text-2xl font-bold leading-none ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
            {active ? formatHours(active.hours).replace(/h$/, '') : formatHours(total).replace(/h$/, '')}
          </p>
          {active && <p className="text-xs text-matdash-muted mt-1">{active.region}</p>}
        </div>
      </div>
      <ul className="w-full space-y-1.5">
        {segments.map((s, i) => (
          <li
            key={s.region}
            className={`flex items-center justify-between gap-3 text-xs rounded px-2 py-1 cursor-pointer transition-colors ${
              hovered === i ? (dark ? 'bg-gray-800' : 'bg-gray-50') : ''
            }`}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <span className={dark ? 'text-gray-300' : 'text-gray-700'}>{s.region}</span>
            </span>
            <span className={`font-semibold ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
              {formatHours(s.hours)}
              <span className={`ml-1 font-normal ${dark ? 'text-gray-500' : 'text-gray-400'}`}>({s.pct}%)</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PagBtn = ({ children, onClick, disabled, active, dark }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-7 h-7 rounded text-xs font-medium transition-colors disabled:opacity-40 ${
      active
        ? 'bg-matdash-primary text-white'
        : dark
          ? 'text-gray-400 hover:bg-gray-700 disabled:hover:bg-transparent'
          : 'text-gray-500 hover:bg-gray-100 disabled:hover:bg-transparent'
    }`}
  >
    {children}
  </button>
);

const SuperAdminDashboard = () => {
  const { dark } = useTheme();
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [utilisation, setUtilisation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRegion, setActiveRegion] = useState('All');
  const [mapHover, setMapHover] = useState('All');
  const [page, setPage] = useState(1);
  const PER_PAGE = 6;

  useEffect(() => {
    const load = async () => {
      try {
        const [tenantResp, utilisationResp] = await Promise.all([
          adminAPI.getTenants(),
          adminAPI.getPlatformUtilisation({ days: 30 }),
        ]);
        setTenants(tenantResp.data?.data || tenantResp.data || []);
        setUtilisation(utilisationResp.data?.data || null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeTenants = tenants.filter((t) => t.status === 'active').length;
  const live = tenants.filter((t) => t.session_status === 'live').length;
  const barData = utilisation?.daily || [];
  const totalHours = utilisation?.total_hours || 0;
  const avgDaily = utilisation?.avg_daily_hours || 0;
  const mostActive = utilisation?.most_active_court;
  const utilisationRate = utilisation?.utilisation_rate || 0;
  const utilisationChange = utilisation?.utilisation_change || 0;
  const regionUtilisation = useMemo(() => {
    const rows = utilisation?.regions || [];
    return rows.map((row, index) => ({
      ...row,
      color: REGION_COLORS[row.region] || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
    }));
  }, [utilisation]);

  const filteredTenants = activeRegion === 'All' ? tenants : tenants.filter((t) => t.region === activeRegion);
  const totalPages = Math.max(1, Math.ceil(filteredTenants.length / PER_PAGE));
  const pageTenants = filteredTenants.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const card = `md-card ${dark ? '!bg-gray-900/95 !border-gray-800' : ''}`;
  const th = `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${dark ? 'text-gray-400' : 'text-matdash-muted'}`;
  const td = `px-4 py-3 text-sm ${dark ? 'text-gray-200' : 'text-matdash-dark'}`;
  const tdSm = `px-4 py-3 text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`;

  const regionCounts = useMemo(() => {
    const counts = {};
    REGION_PINS.forEach((pin) => {
      counts[pin.region] = tenants.filter((t) => t.region === pin.region).length;
    });
    return counts;
  }, [tenants]);

  const highlightedRegion = mapHover === 'All' ? activeRegion : mapHover;

  const welcomeName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Platform Admin'
    : 'Platform Admin';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className={`md-card md-card-primary overflow-hidden xl:col-span-2 ${dark ? '!border-gray-800' : ''}`}>
          <div className="md-card-body p-6 flex items-center gap-4 h-full min-h-[148px]">
            <div className="md-icon-well md-icon-well-lg bg-matdash-primary-subtle text-matdash-primary flex items-center justify-center shrink-0">
              <ScaleIcon className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-matdash-muted mb-1">
                Platform overview
              </p>
              <h5 className="text-matdash-dark text-lg font-semibold leading-snug mb-1">
                Welcome back, {welcomeName}
              </h5>
              <p className="text-matdash-muted text-sm mb-0">
                Monitor tenants, regenerations, and judiciary-wide utilisation from your platform console.
              </p>
            </div>
          </div>
        </div>
        <KpiCard
          icon={BuildingOfficeIcon}
          fill="primary"
          title="Active Court Tenants"
          value={<>{activeTenants} <span className="text-base font-normal text-white/70">/ {tenants.length}</span></>}
          sub="Provisioned"
          meta={`Superior: ${tenants.filter((t) => t.court_type === 'Superior').length}  Lower: ${tenants.filter((t) => t.court_type === 'Lower').length}`}
        />
        <KpiCard
          icon={MicrophoneIcon}
          fill="dark"
          title="Active Recording Sessions"
          value={live}
          sub="Live Courts"
          meta="Real-time audio ingestion"
        />
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <h2 className={`text-sm font-semibold ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>Court Tenants &amp; Region Overview</h2>
          <Link to="/super-admin/tenants" className="text-xs font-medium text-matdash-primary hover:underline">View all →</Link>
        </div>

        <div className={`flex items-center gap-1 px-5 py-3 border-b overflow-x-auto ${dark ? 'border-gray-800 bg-gray-900' : 'border-matdash-border bg-gray-50'}`}>
          {REGIONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                setActiveRegion(r);
                setMapHover('All');
                setPage(1);
              }}
              className={`flex-shrink-0 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                activeRegion === r
                  ? 'bg-matdash-primary text-white'
                  : dark
                    ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                    : 'text-gray-500 hover:bg-white hover:text-gray-800'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row">
          <div className={`flex-shrink-0 lg:w-72 p-4 border-b lg:border-b-0 lg:border-r ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
            <div className="relative max-w-[260px] mx-auto">
              <img src={ghanaMap} alt="Ghana map" className="w-full h-auto rounded-lg" />
              {REGION_PINS.map((pin) => {
                const count = regionCounts[pin.region] || 0;
                const active = highlightedRegion === pin.region;
                return (
                  <button
                    key={pin.region}
                    type="button"
                    onClick={() => {
                      setActiveRegion(pin.region);
                      setMapHover(pin.region);
                      setPage(1);
                    }}
                    onMouseEnter={() => setMapHover(pin.region)}
                    onMouseLeave={() => setMapHover('All')}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 text-[10px] font-bold transition-all ${
                      active ? 'bg-matdash-primary text-white border-white shadow-lg scale-110' : 'bg-white text-matdash-primary border-matdash-primary shadow'
                    }`}
                    style={{ top: pin.top, left: pin.left }}
                    title={`${pin.region}: ${count} courts`}
                  >
                    {count}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-x-auto">
            {loading ? (
              <div className={`p-8 text-center text-sm ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</div>
            ) : (
              <>
                <table className="min-w-full">
                  <thead className={dark ? 'bg-gray-800' : 'bg-gray-50'}>
                    <tr>
                      <th className={th}>#</th>
                      <th className={th}>Court Name</th>
                      <th className={th}>Region</th>
                      <th className={th}>Level</th>
                      <th className={th}>Status</th>
                      <th className={th}>Session Status</th>
                      <th className={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${dark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                    {pageTenants.map((t, i) => (
                      <tr key={t.id} className={`transition-colors ${dark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                        <td className={tdSm}>{(page - 1) * PER_PAGE + i + 1}</td>
                        <td className={`${td} font-medium`}>{t.name}</td>
                        <td className={tdSm}>{t.region}</td>
                        <td className={tdSm}>{t.court_type}</td>
                        <td className="px-4 py-3"><TenantStatusBadge status={t.status} /></td>
                        <td className="px-4 py-3">
                          {t.status !== 'inactive' ? <SessionBadge status={t.session_status} /> : <span className={`text-xs ${dark ? 'text-gray-600' : 'text-gray-400'}`}>—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to="/super-admin/tenants"
                            className={`inline-flex items-center justify-center w-7 h-7 rounded border transition-colors ${
                              dark ? 'border-gray-700 text-gray-400 hover:bg-gray-700' : 'border-gray-200 text-gray-400 hover:bg-gray-50'
                            }`}
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10" />
                            </svg>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className={`flex items-center justify-between px-4 py-3 border-t text-xs ${dark ? 'border-gray-800 text-gray-500' : 'border-matdash-border text-gray-400'}`}>
                  <span>Showing {(page - 1) * PER_PAGE + 1} to {Math.min(page * PER_PAGE, filteredTenants.length)} of {filteredTenants.length} courts</span>
                  <div className="flex items-center gap-1">
                    <PagBtn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} dark={dark}>‹</PagBtn>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
                      <PagBtn key={n} onClick={() => setPage(n)} active={page === n} dark={dark}>{n}</PagBtn>
                    ))}
                    {totalPages > 5 && <span className={dark ? 'text-gray-600' : 'text-gray-300'}>…</span>}
                    <PagBtn onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} dark={dark}>›</PagBtn>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className={`${card} xl:col-span-2`}>
          <div className={`px-5 py-4 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
            <h2 className={`text-sm font-semibold ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>Judiciary-Wide Reporting &amp; Analytics</h2>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className={`text-sm font-medium ${dark ? 'text-gray-200' : 'text-matdash-dark'}`}>Court Utilisation – Hours Recorded</p>
                <div className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1 text-xs text-gray-500"><span className="inline-block w-3 h-2 rounded-sm bg-matdash-primary" /> Superior Courts</span>
                  <span className="flex items-center gap-1 text-xs text-gray-500"><span className="inline-block w-3 h-2 rounded-sm bg-matdash-success" /> Lower Courts</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className={`px-2 py-1 rounded border ${dark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-500'}`}>Last 30 Days ↓</span>
                <span className={`px-2 py-1 rounded border ${dark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-500'}`}>By Court Level ↓</span>
              </div>
            </div>

            <StackedBarChart data={barData} dark={dark} />

            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t ${dark ? 'border-gray-800' : 'border-gray-100'}`}>
              {[
                { label: 'Total Hours (30 Days)', value: formatHours(totalHours) },
                { label: 'Avg. Daily Hours', value: formatHours(avgDaily) },
                {
                  label: 'Most Active Court',
                  value: mostActive
                    ? `${mostActive.court_name}${mostActive.region ? `, ${mostActive.region}` : ''}`
                    : '—',
                },
                {
                  label: 'Utilisation Rate',
                  value: (
                    <>
                      <span className="text-xl font-bold">{utilisationRate}%</span>
                      {utilisationChange !== 0 && (
                        <span className={`text-xs ${utilisationChange > 0 ? 'text-matdash-success' : 'text-red-500'}`}>
                          {utilisationChange > 0 ? '↑' : '↓'} {Math.abs(utilisationChange)}%
                        </span>
                      )}
                    </>
                  ),
                },
              ].map((s) => (
                <div key={s.label}>
                  <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                  <p className={`text-lg font-bold mt-0.5 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={card}>
          <div className={`px-5 py-4 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
            <h2 className={`text-sm font-semibold ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>Court Utilisation by Region</h2>
          </div>
          <div className="p-5">
            {regionUtilisation.length === 0 ? (
              <p className={`text-sm ${dark ? 'text-gray-500' : 'text-gray-400'}`}>No recorded hours yet.</p>
            ) : (
              <DonutChart segments={regionUtilisation} total={totalHours} dark={dark} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
